import { useRef, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Quote,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading2,
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Type your message...',
  minHeight = '300px',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML && value) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value);
    }
  }, [value]);

  useEffect(() => {
    const updateActiveStates = () => {
      setIsActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        justifyLeft: document.queryCommandState('justifyLeft'),
        justifyCenter: document.queryCommandState('justifyCenter'),
        justifyRight: document.queryCommandState('justifyRight'),
      });
    };

    document.addEventListener('selectionchange', updateActiveStates);
    return () => document.removeEventListener('selectionchange', updateActiveStates);
  }, []);

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleChange();
  };

  const handleChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const text = editorRef.current.innerText;
      onChange(html, text);
    }
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      execCommand('insertText', '    ');
    }

    // Keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'z':
          e.preventDefault();
          execCommand('undo');
          break;
        case 'y':
          e.preventDefault();
          execCommand('redo');
          break;
      }
    }
  };

  const ToolbarButton = ({
    command,
    icon: Icon,
    title,
    onClick,
  }: {
    command?: string;
    icon: React.ElementType;
    title: string;
    onClick?: () => void;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      className={`h-8 w-8 p-0 ${command && isActive[command] ? 'bg-muted' : ''}`}
      onClick={onClick || (() => command && execCommand(command))}
      title={title}
      type="button"
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b bg-muted/50 flex-wrap">
        <ToolbarButton command="bold" icon={Bold} title="Bold (Ctrl+B)" />
        <ToolbarButton command="italic" icon={Italic} title="Italic (Ctrl+I)" />
        <ToolbarButton command="underline" icon={Underline} title="Underline (Ctrl+U)" />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          command="formatBlock"
          icon={Heading2}
          title="Heading"
          onClick={() => execCommand('formatBlock', 'h3')}
        />
        <ToolbarButton
          command="formatBlock"
          icon={Quote}
          title="Quote"
          onClick={() => execCommand('formatBlock', 'blockquote')}
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton command="insertUnorderedList" icon={List} title="Bullet List" />
        <ToolbarButton command="insertOrderedList" icon={ListOrdered} title="Numbered List" />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton command="justifyLeft" icon={AlignLeft} title="Align Left" />
        <ToolbarButton command="justifyCenter" icon={AlignCenter} title="Align Center" />
        <ToolbarButton command="justifyRight" icon={AlignRight} title="Align Right" />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton icon={Link} title="Insert Link" onClick={insertLink} />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton command="undo" icon={Undo} title="Undo (Ctrl+Z)" />
        <ToolbarButton command="redo" icon={Redo} title="Redo (Ctrl+Y)" />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 focus:outline-none rich-text-content"
        style={{ minHeight }}
        onInput={handleChange}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      <style>{`
        .rich-text-content:empty:before {
          content: attr(data-placeholder);
          color: rgb(156 163 175);
          pointer-events: none;
          position: absolute;
        }
        .rich-text-content {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }
        .rich-text-content h3 {
          font-size: 1.2em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-content blockquote {
          border-left: 3px solid #e5e7eb;
          padding-left: 1em;
          margin: 0.5em 0;
          color: #6b7280;
        }
        .rich-text-content ul,
        .rich-text-content ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
        .rich-text-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}